import { useEffect, useState } from "react";
import { ExternalLink, Send } from "lucide-react";
import { http, errMsg } from "../../api";
import AttemptBadge from "./AttemptBadge";

export default function ListeningTest({ onChanged }) {
  const [data, setData] = useState(null);
  const [answers, setAnswers] = useState({});
  const [busy, setBusy] = useState("");
  const [notice, setNotice] = useState(null);

  const load = () => http.get("/creator/tests/listening").then(r => setData(r.data)).catch(e => setNotice({ tone: "error", text: errMsg(e) }));
  useEffect(() => { load(); }, []);

  const submit = async lang => {
    setBusy(lang); setNotice(null);
    try {
      const { data: res } = await http.post(`/creator/tests/listening/${encodeURIComponent(lang)}/submit`, { answer: answers[lang] || "" });
      setNotice({ tone: res.attempt.passed ? "success" : "warning", text: `${lang}: ${res.attempt.passed ? "Cleared" : "Not cleared"} · ${res.attempt.percent}% accuracy` });
      load(); onChanged();
    } catch (e) { setNotice({ tone: "error", text: errMsg(e) }); }
    finally { setBusy(""); }
  };

  if (!data) return notice ? <div className="notice error">{notice.text}</div> : <div className="loading">Loading listening test…</div>;
  return (
    <section className="form-card" data-testid="creator-listening-step">
      <h2>Step 3 · Audio listening test</h2>
      <p>Open the shared Drive folder, listen carefully, then enter the dialogue numbers asked for in the instructions — separated by commas (e.g. 3, 7, 12). Order, spacing and repeats don't matter.</p>
      {notice && <div className={`notice ${notice.tone}`} data-testid="listening-notice">{notice.text}</div>}
      <div className="test-list">
        {data.items.map(t => (
          <div className="test-card" key={t.language} data-testid={`listening-test-${t.language}`}>
            <div className="test-card-head"><strong>{t.language}</strong><AttemptBadge attempt={t.attempt} required={t.required} testid={`listening-test-status-${t.language}`} /></div>
            {!t.required && <p className="muted">No listening test is required for {t.language}.</p>}
            {t.required && (
              <>
                {t.instructions && <div className="rich-text small" data-testid={`listening-instructions-${t.language}`}>{t.instructions}</div>}
                {t.drive_link && <a className="ghost" href={t.drive_link} target="_blank" rel="noreferrer" data-testid={`listening-drive-${t.language}`}><ExternalLink size={14} /> Open Drive audio folder</a>}
                {t.can_submit ? (
                  <div style={{ marginTop: 12 }}>
                    <label>Dialogue numbers</label>
                    <textarea rows={2} value={answers[t.language] || ""} onChange={e => setAnswers({ ...answers, [t.language]: e.target.value })} placeholder="e.g. 3, 7, 12, 18" data-testid={`listening-answer-${t.language}`} />
                    <button className="primary" style={{ marginTop: 10 }} disabled={busy === t.language || !(answers[t.language] || "").trim()} onClick={() => submit(t.language)} data-testid={`listening-submit-${t.language}`}><Send size={14} /> {busy === t.language ? "Checking…" : "Submit answers"}</button>
                  </div>
                ) : t.attempt && <div className="result-inline" data-testid={`listening-result-${t.language}`}>Your accuracy: <strong>{t.attempt.percent}%</strong> · {t.attempt.matched} correct dialogue numbers</div>}
              </>
            )}
          </div>
        ))}
      </div>
      {data.status === "unavailable" && <div className="notice warning" data-testid="listening-test-unavailable">The listening test for your languages is not published yet. The admin will set it up soon.</div>}
    </section>
  );
}
